//modules
import React from 'react';
import { gsap } from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CSSRulePlugin } from 'gsap/CSSRulePlugin';
import { NavigationBar } from '../globalComponents/NavigationBar';
import { ProjectsFooter } from '../globalComponents/ProjectsFooter';
import { useTextAnimation } from '../globalComponents/UseTextAnimation';
import { useVideoAnimation } from '../globalComponents/UseVideoAnimation';
import { UpButton } from '../globalComponents/UpButton';

//styles
import './Projects.scss';
import ticketsocket_1 from '../images/ticketsocket_1.webp'
import ticketsocket_2 from '../images/ticketsocket_2.mp4'
import ticketsocket_3 from '../images/ticketsocket_3.webp'
import ticketsocket_4 from '../images/ticketsocket_4.mp4'
import ticketsocket_5 from '../images/ticketsocket_5.webp'
import ticketsocket_6 from '../images/ticketsocket_6.webp'
import ticketsocket_7 from '../images/ticketsocket_7.webp'
import ticketsocket_8 from '../images/ticketsocket_8.webp'
import ticketsocket_9 from '../images/ticketsocket_9.webp'
import ticketsocket_10 from '../images/ticketsocket_10.webp'
import ticketsocket_11 from '../images/ticketsocket_11.webp'
import ticketsocket_12 from '../images/ticketsocket_12.webp'
import ticketsocket_13 from '../images/ticketsocket_13.webp'
import ticketsocket_14 from '../images/ticketsocket_14.webp'
import ticketsocket_15 from '../images/ticketsocket_15.webp'
import ticketsocket_16 from '../images/ticketsocket_16.webp'
import project_vintach from '../images/project_vintach.webp';
import project_aptia from '../images/project_aptia.webp';
import project_disney from '../images/project_disney.webp';
import doldrumdrifters from '../images/doldrumdrifters.webp'

// plugins
gsap.registerPlugin(CSSRulePlugin, ScrollTrigger);

export const Ticketing = () => {

  const StuffData = [ 
    ['Unity Design System', 'White label design system for a benefits administration company.', project_aptia, '/Projects/DesignSystem'],
    ['Vintage Marketplace', 'Branding, Product Ideation & UI Design for a second hand clothing marketplace.', project_vintach, '/Projects/Vintach'],
    ['Figma To Code', 'Low code and design system tools to streamline better hand-off processes at Disney.', project_disney, '/Projects/FigmaToCode'],
    ['Other Stuff', "Personal projects covering illustration, code experiments, and more.", doldrumdrifters, '/OtherStuff'],
 ];

    const { containerRefs, imageRefs } = useVideoAnimation(16);
    const { textRef, headingRef, subtitleRef, bodyRef } = useTextAnimation();


  return (
    <div>
      <div>
      <NavigationBar />

      <div className="subpage-heading" ref={headingRef}>
        <h2 className="heading-2">
            Ticketing Ecosystem.
        </h2>
        <div className="subpage-subtitle">
          <p className="subtitle" ref={(el) => (subtitleRef.current[0] = el)}>Responsabilities</p>
          <p className="subtitle-bold" ref={(el) => (subtitleRef.current[1] = el)}>Product Ideation & UI Design</p>
        </div>
      </div>

      <div className="project-content">

        {/* Content */}
        <div className="project-body">
            <h5 className="heading-5" ref={(el) => (bodyRef.current[0] = el)}>/ Context</h5>
            <p className="project-notes body" ref={(el) => (bodyRef.current[1] = el)}>I led the design direction and execution for a comprehensive ticketing ecosystem for two white-label platforms: TicketSocket and TicketFlip. This ecosystem supported a range of event management services, including seated and timed event storefronts, ticket scanning mobile apps, box office solutions, administrative tools, and analytics.</p>
        </div>

        {/* Image 1 */}
        <section className="main">
            <div ref={containerRefs.current[0]} className="container">
              <div className="img-container">
                  <img className="single-img" ref={imageRefs.current[0]} src={ticketsocket_1} alt="Project Visual 1" />
              </div>
            </div>
        </section>

        {/* Content */}
        <div className="project-body top-spacer">
            <h5 className="heading-5" ref={(el) => (textRef.current[0] = el)}>/ The Problem</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[1] = el)}>For years, TicketSocket has powered large-scale events, but they began facing a growing challenge: every client had a unique business model with specific feature requirements. To meet these needs, they continuously added custom features, leading to increasing code debt and a fragmented system with hardcoded implementations, lacking a scalable way to configure them.</div>
        </div>

        {/* Section Title */}
        <div className="project-body">
            <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[2] = el)}> Ticketing Storefronts. </h3>
        </div>

        {/* Content */}
        <div className="project-body top-spacer">
            <h5 className="heading-5" ref={(el) => (textRef.current[3] = el)}>/ New Experience</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[4] = el)}>To improve the ticket-buying experience, we redesigned storefronts for both recurring and seated events. The new system introduced flexible configurations, allowing each client to tailor features to their specific business model and branding needs. This ensured a more seamless, customized purchasing journey for event organizers and attendees alike.</div>
        </div>
        
        {/* Content */}
        <div className="project-body top-spacer">
            <h5 className="heading-5" ref={(el) => (textRef.current[5] = el)}>/ Recurring Events</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[6] = el)}>This project focused on creating an intuitive user experience for purchasing tickets to multi-date events. The goal was to simplify the selection process, enabling customers to choose their preferred date, time, ticket type, and any add-ons in a seamless flow through to checkout. This versatile design has supported various high-profile immersive experiences, such as National Geographic’s Van Gogh, Monet and King Tut Beyond Experiences, as well as popular events like World of Illumination, 13th Floor, and Funbox.</div>
        </div>

        {/* Image 2 */}
        <section className="main">
            <div ref={containerRefs.current[1]} className="container">
              <div className="img-container">
                  <video className="single-img" ref={imageRefs.current[1]} src={ticketsocket_2} alt="Project Visual 2" autoPlay muted loop/>
              </div>
            </div>
        </section>

        {/* Image 3 */}
        <section className="main">
            <div ref={containerRefs.current[2]} className="container">
              <div className="img-container">
                  <img className="single-img" ref={imageRefs.current[2]} src={ticketsocket_3} alt="Project Visual 3" />
              </div>
            </div>
        </section>

        {/* Content */}
        <div className="project-body top-spacer">
            <h5 className="heading-5" ref={(el) => (textRef.current[7] = el)}>/ Seated Marketplace</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[8] = el)}>One key requirement of the ticketing ecosystem was accommodating seated events, such as sports, concerts, and theater performances. To enhance the purchasing experience, I designed an interactive seat selection process that allowed users to seamlessly compare different sections within the venue. With interactive venue mapping and filtering options, users could easily find and select the best available seats based on their preferences.</div>
        </div>

        {/* Images 10, 11 & 12 */}
        <section className="main row-container medium-gap">
          { /*Image */}
            <div ref={containerRefs.current[3]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[3]} src={ticketsocket_10} alt="Project Visual 12" />
                </div>
            </div>
            <div className="mobile-row">
                {/* Image */}
                <div ref={containerRefs.current[4]} className="multiple-container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[4]} src={ticketsocket_11} alt="Project Visual 13" />
                    </div>
                </div>
                {/* Image */}
                <div ref={containerRefs.current[5]} className="multiple-container hide-desktop">
                    <div className="img-container hide-desktop">
                        <img className="single-img" ref={imageRefs.current[5]} src={ticketsocket_12} alt="Project Visual 14" />
                    </div>
                </div>
            </div>
        </section>

        {/* Content */}
        <div className="project-body top-spacer">
            <h5 className="heading-5" ref={(el) => (textRef.current[9] = el)}>/ Customization</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[10] = el)}>To support the diverse brands using TicketFlip and TicketSocket’s customer-facing products, a robust white-label system was essential. I developed a flexible theming model that enabled detailed customizations and custom templates, allowing each product to be tailored to match the unique look and feel of different brands, ensuring a fully branded experience.</div>
        </div>

        {/* Image 4 */}
        <section className="main">
            <div ref={containerRefs.current[6]} className="container">
              <div className="img-container">
                  <img className="single-img" ref={imageRefs.current[6]} src={ticketsocket_5} alt="Project Visual 5" />
              </div>
            </div>
        </section>

        {/* Image 5 */}
        <section className="main">
            <div ref={containerRefs.current[7]} className="container">
              <div className="img-container">
                  <video className="single-img" ref={imageRefs.current[7]} src={ticketsocket_4} alt="Project Visual 4" autoPlay muted loop/>
              </div>
            </div>
        </section>

        {/* Section Title */}
        <div className="project-body">
            <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[11] = el)}> Configuration Tools. </h3>
        </div>

        {/* Content */}
        <div className="project-body top-spacer">
            <h5 className="heading-5" ref={(el) => (textRef.current[12] = el)}>/ Event Management</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[13] = el)}>To streamline configurations, I designed a centralized administrative portal that allows partners to manage every aspect of their events in one place. From event creation and branding to payment settings, promotions, communications, and customer orders, the portal provides full control and flexibility, enabling partners to tailor their setup based on event types and attendee demographics.</div>
        </div>

        {/* Images 6, 7, 8 & 9 */}
        <section className="main row-container medium-gap">
        { /* In Between Content */}    
            <div className="ui-notes-narrow">
                <p className="body text-right" ref={(el) => (textRef.current[14] = el)}> Tailored Analytics </p>
                <p className="body text-right" ref={(el) => (textRef.current[15] = el)}> Seated Events Configuration </p>
            </div>
          <div className="column-container">
          { /*Image */}
            <div ref={containerRefs.current[8]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[8]} src={ticketsocket_8} alt="Project Visual 6" />
                </div>
            </div>
            {/* Image */}
            <div ref={containerRefs.current[9]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[9]} src={ticketsocket_9} alt="Project Visual 7" />
                </div>
            </div>
          </div>
            <div className="mobile-row">
              <div className="column-container">
                {/* Image */}
                <div ref={containerRefs.current[10]} className="multiple-container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[10]} src={ticketsocket_6} alt="Project Visual 8" />
                    </div>
                </div>
                {/* Image */}
                <div ref={containerRefs.current[11]} className="multiple-container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[11]} src={ticketsocket_7} alt="Project Visual 9" />
                    </div>
                </div>
              </div>
              </div>
              <div className="ui-notes-narrow">
                <p className="body" ref={(el) => (textRef.current[16] = el)}> Configurable Reports </p>
                <p className="body" ref={(el) => (textRef.current[17] = el)}> Recurring Events Configuration </p>
              </div>
        </section>

        {/* Content */}
        <div className="project-body top-spacer">
          <h5 className="heading-5" ref={(el) => (textRef.current[18] = el)}>/ Scanner Web App</h5>
          <div className="project-notes body" ref={(el) => (textRef.current[19] = el)}>To streamline event entry, I worked on a web application for scanning tickets at the venue entrance. The app supported two scanning methods: camera-based QR code scanning and external devices for barcode input, ensuring compatibility with various ticket formats. Additionally, it provided event staff with access to key attendee details, such as order numbers and ticket holder information, enhancing efficiency at check-in.</div>
        </div>

        {/* Images 13, 14, 15 & 16 */}
        <section className="main row-container medium-gap">
        {/*Image */}
        <div className="mobile-row medium-gap">
            {/* Image */}
            <div ref={containerRefs.current[12]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[12]} src={ticketsocket_15} alt="Project Visual 13" />
                </div>
            </div>
            {/* Image */}
            <div ref={containerRefs.current[13]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[13]} src={ticketsocket_16} alt="Project Visual 14" />
                </div>
            </div>
        </div>
        {/*Image */}
        <div className="mobile-row medium-gap">
            {/* Image */}
            <div ref={containerRefs.current[14]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[14]} src={ticketsocket_13} alt="Project Visual 15" />
                </div>
            </div>
            {/* Image */}
            <div ref={containerRefs.current[15]} className="multiple-container">
                <div className="img-container">
                    <img className="single-img" ref={imageRefs.current[15]} src={ticketsocket_14} alt="Project Visual 16" />
                </div>
            </div>
        </div>
        </section>

        {/* Content */}
        <div className="top-spacer last-body">
            <h5 className="heading-5" ref={(el) => (textRef.current[20] = el)}>/ Key Learnings</h5>
            <div className="project-notes body" ref={(el) => (textRef.current[21] = el)}>Working on this ticketing ecosystem provided me with a comprehensive understanding of the diverse range of UX challenges across various user groups. From family event ticket buyers to international basketball fans, and from intuitive front-end experiences to sophisticated admin tools for managing large-scale sales, this project allowed me to explore the full spectrum of the ticketing business. It was a valuable opportunity to grasp the complexities of end-to-end solutions and the many interconnected components that drive success in this space.</div>
        </div>

      </div>

      <ProjectsFooter StuffData={StuffData} />
      
    </div>
    <UpButton />
    </div>
  );
};